// Mock/canned data for the Device Profile (RCA) tab.
//
// This is a straight port of the datasets from the original static mock
// (context-creation-ui/wbc_device_context.html). It is intentionally kept in
// one module so it can be swapped for real API calls when the feature is wired
// up to the backend. Nothing here is fetched — it is all illustrative data.

// ── Header / status banner ──────────────────────────────────────────────
export const deviceSummary = {
  systemId: 'QC-DEV-XYZ-20890',
  make: 'Qualcomm',
  oem: 'HP',
  model: 'SDX',
  referenceTimestamp: '2025-07-10T10:00:00',
};

export const statusBanner = [
  { label: 'Crash Events', value: '2', tone: 'danger' },
  { label: 'Anomalies (Device Detected)', value: 'Unavailable', tone: 'muted' },
  { label: 'Anomalies (Cloud Detected)', value: '5', tone: 'warning' },
  { label: 'Health Score', value: '40 / 100', tone: 'danger', sub: 'Calculated At: 09:00 AM' },
  { label: 'Data Points', value: '924', tone: 'purple' },
];

// Current Session State, Activity Summary, and Device Profile are now fetched
// live from the backend (see DeviceProfile.js) — no mock data for them here.

// ── AI-assisted reasoning ───────────────────────────────────────────────
export const aiReasoning = {
  analysis: {
    icon: '🧠',
    title: 'Diagnostic Data Analysis',
    badge: 'High Confidence: In Evaluation',
    // rendered with <strong> emphasis handled in the component
    summary:
      'Device QC-DEV-XYZ-20890 received a firmware update to v2.4.0 at 9:15 AM. Following the update, memory usage increased steadily, rising from 42% to 71% over the next 20 minutes. A thermal spike was detected at 9:50 AM, with SoC temperature reaching 90°C. At 10:02 AM, a data stall was detected on the modem connection. NAS registration failed shortly after, at 10:04 AM. At 10:20 AM, the device restarted. The sequence of memory growth, thermal spike, and data stall closely precedes the restart.',
  },
  crash: {
    icon: '💥',
    title: 'Crash Analysis',
    badge: '🔍 In Evaluation',
    files: [
      {
        name: 'CRASH-20890-0710-001_micro.dmp',
        rows: [
          ['Crash Area', 'CDSP'],
          ['Crash Error', 'Null pointer dereference'],
          ['Error Signature', 'dsp_handler.c:124  Null pointer dereference'],
          ['Software PL', 'MPSS.DE.7.0-1601-WAIPIO-9'],
          ['Mitigation Recommendation', 'Common issue found on the c2 branch, look to port to latest release'],
          ['Fixed Build', 'MPSS.DE.7.0-1232-WAIPIO-6'],
          ['CR ID', '5842830'],
          ['CR Title', 'Fixing QoS function'],
          ['Matched Image', 'MPSS.DE.4.3'],
          ['Matched Product', 'Waipio.LA.1.0'],
        ],
      },
      {
        name: 'CRASH-20890-0710-001_mini.dmp',
        rows: [
          ['Crash Area', 'CDSP'],
          ['Crash Error', 'Null pointer dereference'],
          ['Error Signature', 'dsp_handler.c:124  Null pointer dereference'],
          ['Software PL', 'MPSS.DE.7.0-1601-WAIPIO-9'],
          ['Mitigation Recommendation', 'Common issue found on the c2 branch, look to port to latest release'],
          ['Fixed Build', 'MPSS.DE.7.0-1232-WAIPIO-6'],
          ['CR ID', '5842830'],
          ['CR Title', 'Fixing QoS function'],
          ['Matched Image', 'MPSS.DE.4.3'],
          ['Matched Product', 'Waipio.LA.1.0'],
        ],
      },
    ],
  },
  log: {
    icon: '📄',
    title: 'Debug Log Analysis',
    badge: '🔍 In Evaluation',
    summary:
      'Phase 1 — canned output. ODL package ODL-20890-0710-A submitted for analysis; awaiting LogTalk integration in AWS.',
  },
};

// ── Unified event timeline ──────────────────────────────────────────────
// `cat` drives the filter pills. `severity` ('critical' | 'warning' | 'highlight')
// tints the row. `badges` are small colored tags. `offset` is signed minutes vs
// the reference event. `detail` supports `code` spans via the {code:[...]} shape
// handled in the component (kept as plain arrays of text/code segments).
export const timelineEvents = [
  {
    id: 'E0', cat: 'health', time: '9:00 AM', offset: -60, dot: 'health',
    name: 'Health Score Computed', badges: [{ t: 'Health', c: 'health' }],
    detail: [{ text: 'Health Score went up from 28 in the previous window to 40 in this window.' }],
  },
  {
    id: 'E0b', cat: 'telemetry', time: '9:00 AM', offset: -60, dot: 'telemetry',
    name: 'UL Rate Reported', badges: [{ t: 'Telemetry', c: 'telemetry' }],
    detail: [
      { text: 'Direction: ' }, { code: 'MQI_TEST_TYPE_UPLINK' },
      { text: '  ·  Peak Rate (Mbps): ' }, { code: '10' },
    ],
  },
  {
    id: 'E0c', cat: 'telemetry', time: '9:00 AM', offset: -60, dot: 'telemetry',
    name: 'DL Rate Reported', badges: [{ t: 'Telemetry', c: 'telemetry' }],
    detail: [
      { text: 'Direction: ' }, { code: 'MQI_TEST_TYPE_DOWNLINK' },
      { text: '  ·  Peak Rate (Mbps): ' }, { code: '5' },
    ],
  },
  {
    id: 'E1', cat: 'mqtt', time: '9:05 AM', offset: -55, dot: 'reg',
    name: 'Device Registration Heartbeat', badges: [{ t: 'MQTT Client Registration', c: 'mqtt' }],
    detail: [
      { text: 'MPSS: ' }, { code: 'MPSS.DE.9.0-01935-KOBUK_GENEMMC_TEST-1:OEM_VER_ROOT...' },
      { text: '  ·  LE Build: ' }, { code: 'CI_Kobuk.LE.1.0-00109.1-EBOOT.QMP.INT-7' },
    ],
    link: { label: '🔎 View More', reg: 'reg1' },
  },
  {
    id: 'E1c', cat: 'odl', time: '9:10 AM', offset: -50, dot: 'odl',
    name: 'ODL Aggregated File Available', badges: [{ t: 'ODL', c: 'odl' }],
    detail: [
      { text: 'Filename: ' }, { code: 'ODL-20890-0710-A.zip' },
      { text: '  ·  Collection Start: 9:10 AM  ·  Collection End: 9:40 AM  ·  Files Aggregated: 3' },
    ],
  },
  {
    id: 'E1b', cat: 'mqtt', time: '9:15 AM', offset: -45, dot: 'reg',
    name: 'Device Registration Heartbeat', badges: [{ t: 'MQTT Client Registration', c: 'mqtt' }],
    detail: [
      { text: 'LE Build updated from ' }, { code: 'CI_Kobuk.LE.1.0-00109.1-EBOOT.QMP.INT-7' },
      { text: ' to ' }, { code: 'CI_Kobuk.LE.2.2-00109.1-EBOOT.QMP.INT-8' }, { text: '.' },
    ],
    detail2: [
      { text: 'MPSS: ' }, { code: 'MPSS.DE.9.0-01935-KOBUK_GENEMMC_TEST-1:OEM_VER_ROOT...' },
      { text: '  ·  LE Build: ' }, { code: 'CI_Kobuk.LE.2.2-00109.1-EBOOT.QMP.INT-8' },
    ],
    link: { label: '🔎 View More', reg: 'reg2' },
  },
  {
    id: 'E5b', cat: 'telemetry', time: '10:00 AM', offset: 0, dot: 'telemetry', severity: 'critical',
    name: 'Modem SSR', badges: [{ t: 'Critical', c: 'crash' }, { t: 'Telemetry', c: 'telemetry' }],
    detail: [{ text: 'Sub System: MODEM  ·  Event: ' }, { code: 'SUBSYSTEM_DOWN' }],
  },
  {
    id: 'E5b2', cat: 'telemetry', time: '10:00 AM', offset: 0, dot: 'telemetry',
    name: 'Modem SSR Recovered', badges: [{ t: 'Telemetry', c: 'telemetry' }],
    detail: [{ text: 'Sub System: MODEM  ·  Event: ' }, { code: 'SUBSYSTEM_UP' }],
  },
  {
    id: 'E5c', cat: 'telemetry', time: '10:01 AM', offset: 1, dot: 'telemetry',
    name: 'PDN Down', badges: [{ t: 'Telemetry', c: 'telemetry' }],
    detail: [
      { text: 'Status: ' }, { code: 'PDN_STATUS_IPV6_DOWN' },
      { text: '  ·  Type: ' }, { code: 'TYPE_IPV4V6' },
      { text: '  ·  Reason: ' }, { code: 'PDN_CALL_END_REASON_TYPE_CALL_MANAGER_DEFINED' },
    ],
  },
  {
    id: 'E5c2', cat: 'telemetry', time: '10:01 AM', offset: 1, dot: 'telemetry',
    name: 'PDN Up', badges: [{ t: 'Telemetry', c: 'telemetry' }],
    detail: [
      { text: 'Status: ' }, { code: 'PDN_STATUS_IPV6_UP' },
      { text: '  ·  Type: ' }, { code: 'TYPE_IPV4V6' },
    ],
  },
  {
    id: 'E6', cat: 'telemetry', time: '10:02 AM', offset: 2, dot: 'anomaly', severity: 'critical',
    name: 'Data Stall Detected', badges: [{ t: 'Critical', c: 'crash' }, { t: 'Telemetry', c: 'telemetry' }],
    detail: [
      { text: 'Sub System: MODEM  ·  Recovery Action: ' }, { code: 'DATA_STALL_RECOVERY_ACTION_MODEM_RLF' },
      { text: '  ·  Recovery Status: ' }, { code: 'DATA_STALL_RECOVERY_STATUS_SUCCESS' },
    ],
  },
  {
    id: 'E6c', cat: 'telemetry', time: '10:03 AM', offset: 3, dot: 'telemetry',
    name: 'Data Stall Recovery Success', badges: [{ t: 'Telemetry', c: 'telemetry' }],
    detail: [{ text: 'Sub System: MODEM  ·  Recovery Action: ' }, { code: 'DATA_STALL_RECOVERY_ACTION_MODEM_RLF' }],
  },
  {
    id: 'E6b', cat: 'telemetry', time: '10:04 AM', offset: 4, dot: 'telemetry',
    name: 'NAS Registration Down', badges: [{ t: 'Telemetry', c: 'telemetry' }],
    detail: [
      { text: 'Rejection Cause: ' }, { code: 'REGISTRATION_EVENT_LIMITED_SERVICE' },
      { text: '  ·  Status: ' }, { code: 'REGISTRATION_STATUS_DOWN' },
    ],
  },
  {
    id: 'E6d', cat: 'telemetry', time: '10:05 AM', offset: 5, dot: 'telemetry',
    name: 'NAS Registration Success', badges: [{ t: 'Telemetry', c: 'telemetry' }],
    detail: [{ text: 'Status: ' }, { code: 'REGISTRATION_STATUS_UP' }],
  },
  {
    id: 'E10', cat: 'crash', time: '10:12 AM', offset: 12, dot: 'crash', severity: 'critical',
    name: 'Microdump Crash (collection started)', badges: [{ t: 'Critical', c: 'crash' }, { t: 'Crash', c: 'crash' }],
    detail: [
      { text: 'Filename: ' }, { code: 'CRASH-20890-0710-001_micro.dmp' },
      { text: '  ·  Crash Area: CDSP  ·  Crash Error: Null pointer dereference' },
    ],
    link: { label: '🔎 View More', crash: 'micro' },
  },
  {
    id: 'E10b', cat: 'crash', time: '10:13 AM', offset: 13, dot: 'crash', severity: 'critical',
    name: 'Minidump Crash (collection started)', badges: [{ t: 'Critical', c: 'crash' }, { t: 'Crash', c: 'crash' }],
    detail: [
      { text: 'Filename: ' }, { code: 'CRASH-20890-0710-001_mini.dmp' },
      { text: '  ·  Crash Area: CDSP  ·  Crash Error: Null pointer dereference' },
    ],
    link: { label: '🔎 View More', crash: 'mini' },
  },
  {
    id: 'E11', cat: 'health', time: '10:15 AM', offset: 15, dot: 'health',
    name: 'Health Score Computed', badges: [{ t: 'Health', c: 'health' }],
    detail: [{ text: 'Health Score dropped from 40 in the previous window to 14 in this window.' }],
  },
  {
    id: 'E12', cat: 'odl', time: '10:16 AM', offset: 16, dot: 'odl',
    name: 'ODL Aggregated File Available', badges: [{ t: 'ODL', c: 'odl' }],
    detail: [
      { text: 'Filename: ' }, { code: 'ODL-20890-0710-B.zip' },
      { text: '  ·  Collection Start: 10:16 AM  ·  Collection End: 12:16 PM  ·  Files Aggregated: 3' },
    ],
  },
  {
    id: 'E13', cat: 'mqtt', time: '10:20 AM', offset: 20, dot: 'crash',
    name: 'Device Restart', badges: [{ t: 'MQTT Client Registration', c: 'mqtt' }],
    detail: [],
  },
  {
    id: 'E13b', cat: 'telemetry', time: '10:21 AM', offset: 21, dot: 'telemetry',
    name: 'Boot KPI', badges: [{ t: 'Telemetry', c: 'telemetry' }],
    detail: [{ text: 'MSS Image Load Start: 20.0s  ·  MSS Out of Reset: 24.0s  ·  Modem In Service: 32.0s' }],
    link: { label: '🔎 View More', bootKpi: true },
  },
  {
    id: 'E13c', cat: 'mqtt', time: '10:22 AM', offset: 22, dot: 'crash',
    name: 'AP Restarted', badges: [{ t: 'MQTT Client Registration', c: 'mqtt' }],
    detail: [],
  },
  {
    id: 'E13d', cat: 'mqtt', time: '10:23 AM', offset: 23, dot: 'crash',
    name: 'Modem Restarted', badges: [{ t: 'MQTT Client Registration', c: 'mqtt' }],
    detail: [],
  },
];

// Reference-event marker shown inline in the timeline.
export const timelineReferenceMarker = {
  afterId: 'E1b', // rendered immediately after this event
  label: '🔴 Reference Event: 10:00 AM — Incident Reported',
};

// Filter pills for the timeline toolbar.
export const timelineFilters = [
  { key: 'all', label: 'All Events', c: 'all' },
  { key: 'telemetry', label: 'Telemetry', c: 'telemetry' },
  { key: 'rt', label: 'RT Telemetry', c: 'rt' },
  { key: 'health', label: 'Health Scores', c: 'health' },
  { key: 'odl', label: 'ODL', c: 'odl' },
  { key: 'adlog', label: 'AD Logs', c: 'adlog' },
  { key: 'adtelemetry', label: 'AD Telemetry', c: 'adtelemetry' },
  { key: 'crash', label: 'Crashes', c: 'crash' },
  { key: 'mqtt', label: 'MQTT Client Registration', c: 'registration' },
  { key: 'connection', label: 'MQTT Connection', c: 'connection' },
];

export const timelineWindowLabel = '± 1hr window';
export const timelineDateLabel = '2025-07-10  ·  9:00 AM – 10:28 AM';

// ── Sub-module anomalies ────────────────────────────────────────────────
export const subModuleAnomalies = [
  {
    key: 'wlan', icon: '📶', iconClass: 'wlan', name: 'WLAN', subtitle: 'Wi-Fi 6E / 802.11ax',
    badge: { text: '3 Warnings', c: 'warning' },
    rows: [
      { dot: 'warning', label: 'Roaming Failure Loop', detail: '5 failed roam attempts between AP-12 and AP-17 within 4 min.', time: '09:20 AM' },
      { dot: 'warning', label: 'RSSI Degradation', detail: 'Signal dropped from −52 dBm to −81 dBm — near edge threshold.', time: '09:25 AM' },
      { dot: 'info', label: 'Channel Congestion', detail: 'Channel 36 utilisation at 87% — auto-channel switch pending.', time: '09:41 AM' },
    ],
    footer: '→ View WLAN Logs',
  },
  {
    key: 'wwan', icon: '📡', iconClass: 'wwan', name: 'WWAN', subtitle: 'NR5G / LTE Modem',
    badge: { text: '1 Critical', c: 'critical' },
    rows: [
      { dot: 'critical', label: 'Modem SSR — Kernel Panic', detail: 'Subsystem restart triggered at 10:00 AM; crash dump filed.', time: '10:00 AM' },
      { dot: 'warning', label: 'NR5G Throughput Drop', detail: 'DL throughput fell from 820 Mbps to 38 Mbps — beam mismatch suspected.', time: '09:47 AM' },
      { dot: 'info', label: 'IMS Registration Delay', detail: 'Re-registration took 22 s post-SSR — SIP 408 timeout observed.', time: '10:04 AM' },
    ],
    footer: '→ View WWAN Logs',
  },
  {
    key: 'eth', icon: '🔌', iconClass: 'eth', name: 'Ethernet', subtitle: 'Physical & Link Layer',
    badge: { text: '2 Critical', c: 'critical' },
    rows: [
      { dot: 'critical', label: 'Link Flap Detected', detail: 'Port eth0 toggled 14× in 3 min — possible cable or PHY fault.', time: '09:18 AM' },
      { dot: 'critical', label: 'RX CRC Error Spike', detail: '1 247 CRC errors in 60 s — exceeds threshold of 100.', time: '09:22 AM' },
      { dot: 'warning', label: 'High TX Queue Latency', detail: 'Average egress queue delay at 38 ms — baseline 5 ms.', time: '09:31 AM' },
    ],
    footer: '→ View Ethernet Logs',
  },
];

// Artifact Quick Access cards are now built live from crash-analysis and
// unified-timeline data (see buildArtifactCards in DeviceProfile.js) — no
// mock data for them here.

// ── Boot KPI modal ──────────────────────────────────────────────────────
export const bootKpiData = [
  { name: 'MSS Image Load Start', seconds: 20.0 },
  { name: 'MSS Out of Reset', seconds: 24.0 },
  { name: 'Modem In Service', seconds: 32.0 },
  { name: 'LAN V4 Assignment', seconds: 147.0 },
  { name: 'LAN V6 Assignment', seconds: 25.0 },
  { name: 'WLAN V4 Assignment', seconds: 147.0 },
  { name: 'WLAN V6 Assignment', seconds: 148.0 },
];

// ── Registration detail modal ───────────────────────────────────────────
export const regTextData = {
  reg1: [
    { name: 'MPSS', value: 'MPSS.DE.9.0-01935-KOBUK_GENEMMC_TEST-1:OEM_VER_ROOT:MPSS.DE.9.0-01935-KOBUK_GENEMMC_TEST-1:OEM_VER_USER' },
    { name: 'LE Build', value: 'CI_Kobuk.LE.1.0-00109.1-EBOOT.QMP.INT-7' },
  ],
  reg2: [
    { name: 'MPSS', value: 'MPSS.DE.9.0-01935-KOBUK_GENEMMC_TEST-1:OEM_VER_ROOT:MPSS.DE.9.0-01935-KOBUK_GENEMMC_TEST-1:OEM_VER_USER' },
    { name: 'LE Build (updated)', value: 'CI_Kobuk.LE.2.2-00109.1-EBOOT.QMP.INT-8' },
    { name: 'LE Build (previous)', value: 'CI_Kobuk.LE.1.0-00109.1-EBOOT.QMP.INT-7' },
  ],
};

// ── Crash detail modal ──────────────────────────────────────────────────
export const crashDetailData = {
  micro: [
    ['Crash Area', 'CDSP'],
    ['Crash Error', 'Null pointer dereference'],
    ['Error Signature', 'dsp_handler.c:124  Null pointer dereference'],
    ['Software PL', 'MPSS.DE.7.0-1601-WAIPIO-9'],
    ['Mitigation Recommendation', 'Common issue found on the c2 branch, look to port to latest release'],
    ['Fixed Build', 'MPSS.DE.7.0-1232-WAIPIO-6'],
    ['CR ID', '5842830'],
    ['CR Title', 'Fixing QoS function'],
    ['Matched Image', 'MPSS.DE.4.3'],
    ['Matched Product', 'Waipio.LA.1.0'],
  ],
  mini: [
    ['Crash Area', 'CDSP'],
    ['Crash Error', 'Null pointer dereference'],
    ['Error Signature', 'dsp_handler.c:124  Null pointer dereference'],
    ['Software PL', 'MPSS.DE.7.0-1601-WAIPIO-9'],
    ['Mitigation Recommendation', 'Common issue found on the c2 branch, look to port to latest release'],
    ['Fixed Build', 'MPSS.DE.7.0-1232-WAIPIO-6'],
    ['CR ID', '5842830'],
    ['CR Title', 'Fixing QoS function'],
    ['Matched Image', 'MPSS.DE.4.3'],
    ['Matched Product', 'Waipio.LA.1.0'],
  ],
};

// ── Dense telemetry chart modal ─────────────────────────────────────────
// `points` are [minuteOffsetFromWindowStart, value] pairs for line charts.
// `chartType: 'histogram'` entries use `bins` instead.
export const denseTelemetryChips = [
  { key: 'thermal', icon: '🌡️', label: 'Thermal' },
  { key: 'flash', icon: '💾', label: 'Flash' },
  { key: 'memory', icon: '🧠', label: 'Memory' },
  { key: 'cpu', icon: '⚙️', label: 'CPU' },
  { key: 'rsrp', icon: '📡', label: 'RSRP' },
  { key: 'rsrq', icon: '📡', label: 'RSRQ' },
  { key: 'sinr', icon: '📡', label: 'SINR' },
];

export const tsData = {
  thermal: { title: 'Thermal', unit: '°C', min: 30, max: 95, points: [[0, 38], [10, 40], [23, 44], [36, 65], [45, 90], [57, 88], [67, 85], [75, 38], [83, 38]] },
  flash: { title: 'Flash', unit: '% used', min: 0, max: 20, points: [[0, 9], [10, 9], [23, 9], [36, 9], [45, 9], [57, 9], [63, 11], [67, 12], [75, 10], [83, 10]] },
  memory: { title: 'Memory', unit: '% used', min: 30, max: 80, points: [[0, 40], [10, 42], [23, 55], [36, 65], [45, 71], [57, 73], [67, 75], [75, 45], [83, 44]] },
  cpu: { title: 'CPU', unit: '% util', min: 20, max: 100, points: [[0, 35], [10, 40], [23, 65], [36, 89], [45, 95], [57, 97], [67, 90], [75, 30], [83, 32]] },
  rsrp: {
    title: 'RSRP', unit: 'dBm', chartType: 'histogram', bins: [
      { label: '-120 to -110', count: 2 }, { label: '-110 to -100', count: 6 }, { label: '-100 to -90', count: 14 },
      { label: '-90 to -80', count: 28 }, { label: '-80 to -70', count: 19 }, { label: '-70 to -60', count: 5 },
    ],
  },
  rsrq: {
    title: 'RSRQ', unit: 'dB', chartType: 'histogram', bins: [
      { label: '-20 to -17', count: 3 }, { label: '-17 to -14', count: 9 }, { label: '-14 to -11', count: 21 },
      { label: '-11 to -8', count: 26 }, { label: '-8 to -5', count: 13 }, { label: '-5 to -3', count: 2 },
    ],
  },
  sinr: {
    title: 'SINR', unit: 'dB', chartType: 'histogram', bins: [
      { label: '-10 to -5', count: 4 }, { label: '-5 to 0', count: 10 }, { label: '0 to 5', count: 15 },
      { label: '5 to 10', count: 24 }, { label: '10 to 15', count: 18 }, { label: '15 to 20', count: 9 },
      { label: '20 to 25', count: 5 }, { label: '25 to 30', count: 2 },
    ],
  },
};

// Window metadata for converting point minute-offsets to clock labels.
export const tsWindow = { start: '9:05 AM', end: '10:28 AM', totalMin: 83, refMin: 55, refLabel: '10:00 AM', startTotalMin: 9 * 60 + 5 };
